<script setup lang="ts">
import { FormLabel, FormInput, FormCheck } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Button from "../../../base-components/Button";
import Lucide from "../../../base-components/Lucide";
import Preview from "../../../base-components/Preview";
import { onBeforeMount, ref } from "vue";
import { reactive, toRefs } from "vue";
import { required, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { userUserStore } from "../../../stores/user";
import { useMensajesStore } from "../../../services/mensajes";
import { useUsuariosStore } from "../../../services/usuarios";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where
} from "firebase/firestore";
import TomSelect from "../../../custom-components/TomSelect";
import { useUsuarioRolesStore } from "../../../services/usuarioRoles";
import { useUsuariosInvitacionesStore } from "../../../services/usuariosInvitaciones";
import { LogAcciones, UsuariosSistemaClave } from "@fixeduy/restaurantestypes";
import { agregarUsuarioConClave } from "../../../services/cloudFunctions/agregarUsuarioConClave";
import { getCurrentTime } from "../../../utils/fecha";
import { useLogAccionesStore } from "../../../services/logAcciones";

const userStore = userUserStore();
const usuariosStore = useUsuariosStore();
const usuariosRolesStore = useUsuarioRolesStore();
const logAccionesStore = useLogAccionesStore();
const cargando = ref(false);
const verPassword = ref(false);
const verConfirmarPassword = ref(false);

onBeforeMount(() => {
  usuariosRolesStore.getRolesFromFirestore();
});

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const formData = reactive({
  nombre: "",
  usuario: "",
  clave: "",
  claveConfirmar: "",
  rol: "Admin",
  vendedor: true,
  vendedorRestringido: false,
  locales: []
});

const rules = {
  nombre: {
    required: helpers.withMessage("El nombre no puede estar vacío", required)
  },
  usuario: {
    required: helpers.withMessage("El usuario no puede estar vacío", required),
    sinArroba: {
      $validator: (str: string) => {
        const regexArroba = /@/;
        return !regexArroba.test(str);
      },
      $message: "El usuario no puede contener @"
    }
  },
  rol: {
    required: helpers.withMessage("El rol no puede estar vacío", required)
  },
  vendedor: {},
  clave: {
    required: helpers.withMessage("La contraseña no puede ser vacía", required),
    unaLetraMin: {
      $validator: (str: string) => {
        const regexLetraMin = /[a-z]/;
        return regexLetraMin.test(str);
      },
      $message: "Debe de contener al menos una letra miniscula"
    },
    unaLetraMay: {
      $validator: (str: string) => {
        const regexLetraMin = /[A-Z]/;
        return regexLetraMin.test(str);
      },
      $message: "Debe de contener al menos una letra mayúscula"
    },
    unNumero: {
      $validator: (str: string) => {
        const regexLetraMin = /[0-9]/;
        return regexLetraMin.test(str);
      },
      $message: "Debe de contener al menos un número"
    },
    largo: {
      $validator: (str: string) => {
        return str.length >= 8;
      },
      $message: "Debe de contener al menos 8 caracteres"
    }
  },
  claveConfirmar: {
    coincidir: {
      $validator: (str: string) => {
        return str == formData.clave;
      },
      $message: "Las contraseñas no coinciden"
    }
  },
  vendedorRestringido: {},
  locales: {}
};

const validate = useVuelidate(rules, toRefs(formData));
const mensajesStore = useMensajesStore();

const handlerPassword = () => {
  verPassword.value = !verPassword.value;
};

const handlerConfirmaPassword = () => {
  verConfirmarPassword.value = !verConfirmarPassword.value;
};

const validarExisteUsuario = async (mail: string): Promise<boolean> => {
  const db = getFirestore();
  const collectionRoute = `empresas/${userStore.empresa?.id}/usuarios/`;
  const existe = await getDocs(
    query(collection(db, collectionRoute), where("mail", "==", mail))
  );

  return existe.docs.length > 0;
};

const handlerCreate = async () => {
  const result = await validate.value.$validate();

  if (result) {
    cargando.value = true;

    let usuario: UsuariosSistemaClave = {
      id: "",
      nombre: formData.nombre,
      rolId: formData.rol,
      usuario: formData.usuario,
      clave: formData.clave
    };
    const resultado = await agregarUsuarioConClave(usuario);
    if (resultado.exito) {
      const logAcciones: LogAcciones = {
        id: "",
        accion: [`Se agrega usuario con clave: ${usuario.usuario}`],
        modulo: "usuarios",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };

      await logAccionesStore.guardarLog(logAcciones);

      await usuariosStore.getFromFirestore();
      mensajesStore.mostrarModal(
        "Usuario creado",
        "Ya puede iniciar sesión en acceso.atiendo.cloud, con RUT, usuario y clave.",
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    } else {
      mensajesStore.mostrarModal(
        "Error al crear usuario",
        resultado.errorMsg,
        mensajesStore.mensajeType.error
      );
    }
  }
  cargando.value = false;
};
</script>

<template>
  <Preview class="box mt-5">
    <form class="validate-form p-4" @submit.prevent="handlerCreate">
      <div
        class="border-b sm:flex-row border-slate-200/60 dark:border-darkmode-400"
      >
        <h2 class="mr-auto text-lg font-medium">Agregar usuario con clave</h2>
        <div class="col-span-12 lg:col-span-6">
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Nombre
              </FormLabel>
            </div>
            <div class="flex-1">
              <FormInput
                type="text"
                v-model.trim="validate.nombre.$model"
                :class="{
                  'border-danger': validate.nombre.$error
                }"
                placeholder="Nombre"
              />
              <template v-if="validate.nombre.$error">
                <div
                  v-for="(error, index) in validate.nombre.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>

          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Usuario
              </FormLabel>
            </div>
            <div class="flex-1">
              <FormInput
                type="text"
                v-model.trim="validate.usuario.$model"
                :class="{
                  'border-danger': validate.usuario.$error
                }"
                placeholder="Usuario"
              />
              <template v-if="validate.usuario.$error">
                <div
                  v-for="(error, index) in validate.usuario.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
          </div>

          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Contraseña
              </FormLabel>
            </div>
            <div>
              <FormInput
                v-model="formData.clave"
                class="block w-80 mx-auto"
                :type="verPassword ? 'text' : 'password'"
              />
              <template v-if="validate.clave.$error">
                <div
                  v-for="(error, index) in validate.clave.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
            <Lucide
              @click="handlerPassword"
              icon="Eye"
              class="w-4 h-4 m-3 cursor-pointer"
            />
          </div>
          <div class="flex mt-2">
            <div class="flex-none w-50 text-right pr-5">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
              </FormLabel>
            </div>
            <div>
              <p>
                La contraseña debe contener al menos una letra minúscula, una
                letra mayúscula y un número, con un mínimo de 8 caracteres.
              </p>
            </div>
          </div>
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5 mt-2">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Confirma la contraseña
              </FormLabel>
            </div>
            <div>
              <FormInput
                v-model="formData.claveConfirmar"
                class="block w-80 mx-auto"
                :type="verConfirmarPassword ? 'text' : 'password'"
              />
              <template v-if="validate.claveConfirmar.$error">
                <div
                  v-for="(error, index) in validate.claveConfirmar.$errors"
                  :key="index"
                  class="mt-2 text-danger"
                >
                  {{ error.$message }}
                </div>
              </template>
            </div>
            <Lucide
              @click="handlerConfirmaPassword"
              icon="Eye"
              class="w-4 h-4 m-3 cursor-pointer"
            />
          </div>

          <!-------------------- rol ------------------------>
          <div class="flex mt-5">
            <div class="flex-none w-50 text-right pr-5">
              <FormLabel htmlFor="horizontal-form-1" class="w-40 sw:w-20">
                Rol
              </FormLabel>
            </div>
            <div class="flex-1">
              <TomSelect
                v-model.trim="formData.rol"
                :class="{
                  'border-danger': validate.rol?.$error
                }"
                style="width: 50%"
                :options="{ placeholder: 'Selecciona el rol' }"
              >
                <template v-for="rol in usuariosRolesStore.roles" :key="rol.id">
                  <option :value="rol.id">{{ rol.rol }}</option>
                </template>
              </TomSelect>
            </div>
          </div>
        </div>
        <p class="mt-5">
          El usuario podrá acceder en acceso.atiendo.cloud, mediante RUT,
          usuario y contraseña. Recomendamos crear usuarios con la dirección de
          mail, ya que es más seguro y sencillo.
        </p>
        <div v-if="!cargando" class="flex mt-6">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="
                () => {
                  emit('cerrar');
                }
              "
            >
              Cancelar</Button
            >
          </div>
          <div class="flex-1 text-right">
            <Button variant="primary" class="mb-2 mr-1" @click="handlerCreate">
              Agregar nuevo usuario con clave
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </form>
  </Preview>
</template>
